import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../ui/button";
import ChannelSwitcher from "../ChannelSwitcher";
import TopUpOverlayDialog from "../TopUpOverlayDialog";
import {
  LogIn, LogOut, Settings, Coins, Plus, Crown,
  AlertTriangle, Bell, Inbox as InboxIcon, Bookmark,
} from "lucide-react";

const LOW_THRESHOLD = 15;

/**
 * HeaderDesktopUserMenu — the right-side cluster on viewports ≥ xl.
 * Renders the logged-in toolbar (channel switcher, inbox, dashboard,
 * admin shortcut for admins, balance pill, sign-out) OR the
 * "Get Started" CTA when no user is signed in.
 *
 * Props:
 *   user           — current auth user (null = signed out state)
 *   logout         — auth-context logout fn
 *   newFollowers   — count from useHeaderNotifications
 *   inboxUnread    — count from useHeaderNotifications
 *   expiredAlertsUnread — count from useHeaderNotifications (bell badge)
 *   creditsBlink   — true for 600 ms after a credits-deducted event
 *   onOpenAuth     — pops the shared auth dialog
 */
const HeaderDesktopUserMenu = ({
  user, logout, newFollowers, inboxUnread, expiredAlertsUnread, creditsBlink, onOpenAuth,
}) => {
  // Top-up overlay state must live at the component top per the Rules
  // of Hooks — the signed-out early return below would otherwise skip
  // this hook and React would explode on the next mount.
  const [topUpOpen, setTopUpOpen] = useState(false);
  if (!user) {
    return (
      <Button
        className="bg-emerald-600 hover:bg-emerald-700"
        onClick={onOpenAuth}
        data-testid="get-started-btn"
      >
        <LogIn className="h-4 w-4 mr-2" /> Get Started
      </Button>
    );
  }
  const low = user.credits < LOW_THRESHOLD;
  const isProducer = user.subscription_tier === "producer";
  // Producer-tier subscribers expect the balance pill to open an
  // in-place top-up overlay so they don't lose their current page
  // context (Jun 7 spec). Everyone else still navigates to /pricing
  // — they need to see the tier ladder before topping up makes sense.
  return (
    <>
      <ChannelSwitcher />
      <Link to="/marketplace/expired" className="relative" data-testid="nav-expired-alerts" title="Expired Marketplace alerts">
        <Button variant="outline" size="sm" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10">
          <Bell className="h-4 w-4" />
        </Button>
        {expiredAlertsUnread > 0 && (
          <span data-testid="expired-alerts-badge"
            title={`${expiredAlertsUnread} expired video${expiredAlertsUnread === 1 ? '' : 's'} you once liked ${expiredAlertsUnread === 1 ? 'is' : 'are'} for sale`}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-black text-[10px] font-bold flex items-center justify-center border-2 border-black shadow-lg animate-pulse">
            {expiredAlertsUnread > 99 ? "99+" : expiredAlertsUnread}
          </span>
        )}
      </Link>
      <Link to="/inbox" className="relative" data-testid="nav-inbox" title="Inbox">
        <Button variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
          <InboxIcon className="h-4 w-4" />
        </Button>
        {inboxUnread > 0 && (
          <span data-testid="inbox-unread-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold flex items-center justify-center border-2 border-black shadow-lg">
            {inboxUnread > 99 ? "99+" : inboxUnread}
          </span>
        )}
      </Link>
      <Link to="/trail" data-testid="nav-trail" title="My Trail">
        <Button variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
          <Bookmark className="h-4 w-4" />
        </Button>
      </Link>
      <Link to="/dashboard" className="relative">
        <Button variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" data-testid="nav-dashboard" title="Dashboard" aria-label="Dashboard">
          <Settings className="h-4 w-4" />
        </Button>
        {newFollowers > 0 && (
          <span data-testid="new-followers-badge"
            title={`${newFollowers} new follower${newFollowers === 1 ? "" : "s"}`}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-black shadow-lg animate-pulse">
            +{newFollowers > 99 ? "99" : newFollowers}
          </span>
        )}
      </Link>
      {user.is_admin && (
        <Link to="/admin" data-testid="nav-admin">
          <Button variant="outline" size="sm" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
            <Crown className="h-4 w-4 mr-1" /> Admin
          </Button>
        </Link>
      )}
      {isProducer ? (
        <button
          type="button"
          title={low ? `Low balance: ${user.credits.toFixed(2)} — tap to top up` : "Tap to top up credits"}
          data-testid="credit-balance-link"
          data-low-balance={low ? "true" : "false"}
          onClick={() => setTopUpOpen(true)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${creditsBlink ? "credits-blink" : ""} ${
            low
              ? "bg-amber-500/15 border-amber-500/50 hover:bg-amber-500/25 hover:border-amber-400"
              : "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-400"
          }`}
        >
          {low ? <AlertTriangle className="h-4 w-4 text-amber-400" /> : <Coins className="h-4 w-4 text-emerald-400" />}
          <span className={`font-semibold ${low ? "text-amber-400" : "text-emerald-400"}`} data-testid="credit-balance">
            {user.credits.toFixed(2)}
          </span>
          <Plus className={`h-3 w-3 ${low ? "text-amber-400/80" : "text-emerald-400/70"}`} />
        </button>
      ) : (
        <Link to="/pricing"
          title={low ? `Low balance: ${user.credits.toFixed(2)} — tap to top up` : "Tap to top up credits"}
          data-testid="credit-balance-link"
          data-low-balance={low ? "true" : "false"}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${creditsBlink ? "credits-blink" : ""} ${
            low
              ? "bg-amber-500/15 border-amber-500/50 hover:bg-amber-500/25 hover:border-amber-400"
              : "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-400"
          }`}
        >
          {low ? <AlertTriangle className="h-4 w-4 text-amber-400" /> : <Coins className="h-4 w-4 text-emerald-400" />}
          <span className={`font-semibold ${low ? "text-amber-400" : "text-emerald-400"}`} data-testid="credit-balance">
            {user.credits.toFixed(2)}
          </span>
          <Plus className={`h-3 w-3 ${low ? "text-amber-400/80" : "text-emerald-400/70"}`} />
        </Link>
      )}
      <Button variant="ghost" size="sm" onClick={logout} className="text-gray-400 hover:text-white" data-testid="logout-btn">
        <LogOut className="h-4 w-4" />
      </Button>
      <TopUpOverlayDialog open={topUpOpen} onOpenChange={setTopUpOpen} />
    </>
  );
};

export default HeaderDesktopUserMenu;
